import React, {useEffect, useState} from "react";
// import {createVeriffFrame} from "@veriff/incontext-sdk";
import {createVeriffFrame} from "../utils";
import {
  getIdUploadStatus,
  getVeriffSession,
  proxyOptimizelyEventTracking,
  sendBackendEvent,
  setDeviceTags,
  setEscapeHatchReason,
  setIdUploadStatus
} from "../constants/ApiCalls";
import {Routes} from "../constants/Routing";
import {OutlinedButton, SpinnerLoader} from "scuid-x";
import {GenErrorPageWithButton} from "../components/GenErrorPageWithButton";
import {MeasurementEvents} from "../constants/Measurement";
import {DOC_CAPTURE_PAGE_LANDED_EVENT} from "../constants/Experiments";
import {DeviceDetection} from "../constants/DeviceDetection";
import {IdUploadStatus} from "../constants/IdUploadStatus";
import "./Veriff.css";
import {IDVMethod} from "../constants/IDVMethod";
import {EscapeHatchReason} from "../constants/EscapeHatchReason";

export interface VeriffProps {
  appId?: string;
  uuid?: string;
  coBorrower?: string;
  docType?: string;
}
export const Veriff = (props: VeriffProps) => {
  const [error, setError] = useState(EscapeHatchReason.NO_ERROR);
  const [cancel, setCancel] = useState(false);
  const [finished, setFinished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [optimizelyTrackingSent, setOptimizelyTrackingSent] = useState(false);

  const onManualUploadPress = () => {
    (async () => {
      await setEscapeHatchReason(props.uuid, props.appId, props.coBorrower, props.docType,
          EscapeHatchReason.CANCELED_VERIFF);
    })();
    Routes.goToEscapeHatch(props.uuid, props.appId, props.docType);
  };

  useEffect(() => {
    if (!optimizelyTrackingSent) {
      proxyOptimizelyEventTracking(
          props.uuid,
          props.appId,
          DOC_CAPTURE_PAGE_LANDED_EVENT,
          props.coBorrower
      );
      setOptimizelyTrackingSent(true);
    }
  });
  useEffect(() => {
    const { appId, uuid, coBorrower, docType } = props;
    (async () => {
      const idUploadStatus = await getIdUploadStatus(uuid, appId, coBorrower, docType);
      if (idUploadStatus && idUploadStatus.status === IdUploadStatus.FINISHED) {
        setFinished(true);
        return null;
      }
      try {
        let sessionUrl = "";
        try {
          const response = await getVeriffSession(uuid, appId, coBorrower, docType);
          sessionUrl = response?.data?.session?.url;
          if (!sessionUrl) {
            throw new Error("No Veriff session URL returned.");
          }
        } catch (e) {
          // Veriff was down or biometric-verification was not enable to create a valid Veriff session.
          return setError(EscapeHatchReason.VERIFF_ERROR);
        }
        setUrl(sessionUrl);
        await Promise.all([
          setIdUploadStatus(uuid, appId, coBorrower, docType, IdUploadStatus.INITIATED),
          setDeviceTags(
              uuid,
              appId,
              coBorrower,
              docType,
              IDVMethod.VERIFF,
              IdUploadStatus.INITIATED,
              navigator.mediaDevices.enumerateDevices()
          )
        ]);
        sendBackendEvent(
            uuid,
            appId,
            MeasurementEvents.EV_ID_LAUNCH_VERIFF,
            coBorrower,
            {
              document_type: docType,
              device: DeviceDetection.getDataDogDeviceTags()
            }
        );
        setLoading(false);

        return createVeriffFrame({
          url: sessionUrl,
          onEvent: veriffCallback,
          embedded: true,
          embeddedOptions: {
            // Specifies the parent DOM element of Veriff’s iframe, using its ID attribute
            rootElementID: 'veriff-iframe-container',
          },
        });
      } catch (e) {
        // UI error has occurred.
        return setError(EscapeHatchReason.UI_ERROR);
      }
    })();
  }, []);
  useEffect(() => {
    (async () => {
      if (finished) {
        Routes.goToFinished();
      }
    })();
  }, [finished]);
  useEffect(() => {
    if (error !== EscapeHatchReason.NO_ERROR) {
      (async () => {
        await setEscapeHatchReason(props.uuid, props.appId, props.coBorrower, props.docType, error);
      })();
      return Routes.goToUploadManual(props.uuid, props.appId, props.docType);
    }
  }, [error]);

  const veriffCallback = async (event: any) => {
    switch (event) {
      case "STARTED":
        await setIdUploadStatus(props.uuid, props.appId, props.coBorrower, props.docType,
            IdUploadStatus.STARTED);
        return;
      case "CANCELED":
        setLoading(true);
        sendBackendEvent(
            props.uuid,
            props.appId,
            MeasurementEvents.EV_ID_VERIFF_RESULT,
            props.coBorrower,
            {
              status: "cancel",
              document_type: props.docType,
              device: DeviceDetection.getDataDogDeviceTags()
            }
        );
        await Promise.all([
          setIdUploadStatus(props.uuid, props.appId, props.coBorrower, props.docType,
              IdUploadStatus.CANCELED),
          setDeviceTags(
              props.uuid,
              props.appId,
              props.coBorrower,
              props.docType,
              IDVMethod.VERIFF,
              IdUploadStatus.CANCELED,
              navigator.mediaDevices.enumerateDevices()
          )
        ]);
        setLoading(false);
        setCancel(true);
        return;
      case "FINISHED":
        setLoading(true);
        sendBackendEvent(
            props.uuid,
            props.appId,
            MeasurementEvents.EV_ID_VERIFF_RESULT,
            props.coBorrower,
            {
              status: "success",
              document_type: props.docType,
              device: DeviceDetection.getDataDogDeviceTags()
            }
        );
        await Promise.all([
          setIdUploadStatus(props.uuid, props.appId, props.coBorrower, props.docType,
              IdUploadStatus.FINISHED),
          setDeviceTags(
              props.uuid,
              props.appId,
              props.coBorrower,
              props.docType,
              IDVMethod.VERIFF,
              IdUploadStatus.FINISHED,
              navigator.mediaDevices.enumerateDevices()
          )
        ]);
        setLoading(false);
        setFinished(true);
        return;
    }
  };
  if (cancel) return (
      <GenErrorPageWithButton
          title={"We’ll need photos of your ID to continue"}
          description={"You may try again, or if you are having trouble taking a picture, consider uploading your ID directly instead."}
          buttonTitle={"Retry"}
          onButtonPress={() => {
            setCancel(false);
            sendBackendEvent(
                props.uuid,
                props.appId,
                MeasurementEvents.EV_ID_VERIFF_RESULT,
                props.coBorrower,
                {
                  status: "retry",
                  document_type: props.docType,
                  device: DeviceDetection.getDataDogDeviceTags()
                }
            );
            setLoading(true);
            (async () => {
              await Promise.all([
                setIdUploadStatus(props.uuid, props.appId, props.coBorrower, props.docType,
                    IdUploadStatus.RETRY),
                setDeviceTags(
                    props.uuid,
                    props.appId,
                    props.coBorrower,
                    props.docType,
                    IDVMethod.VERIFF,
                    IdUploadStatus.RETRY,
                    navigator.mediaDevices.enumerateDevices()
                )
              ]);
            })();
            setLoading(false);

            return createVeriffFrame({
              url,
              onEvent: veriffCallback,
              embedded: true,
              embeddedOptions: {
                rootElementID: 'veriff-iframe-container'
              }
            });
          }}
          child={<OutlinedButton className={"Veriff-manual-upload-button"} onClick={onManualUploadPress}>
            Upload directly instead
          </OutlinedButton>}
      />);
  else if (loading) return <SpinnerLoader isFullPage={true} />;
  else {
    return null;
  }
};



// WEBPACK FOOTER //
// ./src/screens/Veriff.tsx